import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputCard } from '@components/cards';
import { InputCardGroup } from '@components/cards/input-card-group';
import { EmptyBlock } from '@components/empty-block';
import { IconEmpty } from '@components/icons/icon-empty';
import { Loader } from '@components/loader';
import { UserContext } from '@contexts/user/user-context';
import { useGetAdminUsers } from '@hooks/use-get-admin-users/use-get-admin-users';
import { isSelfUser } from '@utils/is-self-user/is-self-user';

import { ImpersonateRoleCards } from './impersonate-role-cards';
import { ImpersonateUserListContentProps } from './impersonate-user-modal.type';

export const ImpersonateUserListContent = ({
  email,
  setSelectedUser,
  companyId,
  onImpersonate,
}: ImpersonateUserListContentProps) => {
  const { t } = useTranslation();
  const { userState } = useContext(UserContext);
  const { data, isLoading } = useGetAdminUsers({ email }, { enabled: !!email, disableLoading: true });
  const [isChecked, setIsChecked] = useState(false);

  // Each new result starts unselected: the parent only clears its selection on close, so without this a
  // pick from the previous result — or your own blocked account shown now — would stay confirmable.
  useEffect(() => {
    setIsChecked(false);
    setSelectedUser(undefined);
  }, [data?.uuid, setSelectedUser]);

  const isSelf = isSelfUser(userState?.id, data?.uuid);

  const handleClick = () => {
    setIsChecked((prev) => !prev);
    // Mirror the toggle: deselecting has to clear the parent too, or a card the admin unchecked stays
    // confirmable through the still-enabled Done.
    setSelectedUser(isChecked ? undefined : data);
  };

  const isSearching = !!email;

  return (
    <div className='mt-6'>
      {!isSearching && companyId && onImpersonate && (
        <ImpersonateRoleCards companyId={companyId} onImpersonate={onImpersonate} />
      )}
      {isLoading && <Loader />}
      {!data && isSearching && (
        <div data-testid='impersonate-modal--list-empty'>
          <EmptyBlock title={'No email, no party'}>
            <IconEmpty className='my-3 text-gray-500 dark:text-white' />
          </EmptyBlock>
        </div>
      )}
      <output
        // Mounted whatever the result: a live region mounted together with its text may not be announced.
        className='block'>
        {data && isSelf && (
          // Neutral line, not the Error style: nothing went wrong.
          <p className='text-body-s text-secondary text-left' data-testid='impersonate-modal--self-reason'>
            {t('common:impersonateSelfReason')}
          </p>
        )}
      </output>
      {data && !isSelf && (
        <InputCardGroup ariaLabel='Users'>
          <InputCard isChecked={isChecked} isDisabled={false} handleClick={handleClick}>
            <div data-testid='impersonate-modal--list-content'>
              <p className='text-base text-left font-bold text-primary'>{data.fullName}</p>
              <p className='text-sm text-left text-gray-700 dark:text-white'>{data.email}</p>
            </div>
          </InputCard>
        </InputCardGroup>
      )}
    </div>
  );
};
